import { useQuery } from "@tanstack/react-query";
import { Globe, Link2, Megaphone, Save, Send } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";

type MetaForm = {
  id: string;
  name: string | null;
  label: string | null;
  enabled: boolean;
  status: string | null;
};
type MetaSettings = {
  routing?: "round_robin" | "none";
  participants?: number[];
  alert_hours?: number;
};
type MetaStatus = {
  state: {
    synced_at?: string;
    last_error?: string | null;
    last_error_at?: string;
  } | null;
  configured: boolean;
  week: number;
  today: number;
  last_lead_at: string | null;
  forms: Record<string, number>;
};

/**
 * "Connectors" tab of the settings: where leads come from. Meta Lead Ads works
 * today; the site form and Telegram are next. Tokens never reach the browser:
 * the tab shows state and edits forms and routing only.
 */
export const SettingsConnectors = () => {
  const translate = useTranslate();
  const [open, setOpen] = useState<"meta" | "telegram" | null>("meta");
  const telegramStatus = useTelegramStatus();
  const status = useMetaStatus();
  const metaState = status.data
    ? status.data.state?.last_error
      ? "error"
      : status.data.configured
        ? "on"
        : "off"
    : "off";

  return (
    <Card>
      <CardContent className="space-y-5">
        <div>
          <h2 className="text-xl font-semibold text-muted-foreground">
            {translate("crm.settings.connectors.title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.connectors.description")}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <ConnectorTile
            icon={<Megaphone className="size-5" />}
            name="Meta Lead Ads"
            hint={translate("crm.settings.connectors.meta.hint")}
            state={metaState}
            active={open === "meta"}
            onClick={() => setOpen(open === "meta" ? null : "meta")}
          />
          <ConnectorTile
            icon={<Globe className="size-5" />}
            name={translate("crm.settings.connectors.site.name")}
            hint={translate("crm.settings.connectors.site.hint")}
            state="soon"
          />
          <ConnectorTile
            icon={<Send className="size-5" />}
            name="Telegram"
            hint={translate("crm.settings.connectors.telegram.hint")}
            state={telegramStatus.data?.configured ? "on" : "off"}
            active={open === "telegram"}
            onClick={() => setOpen(open === "telegram" ? null : "telegram")}
          />
        </div>
        {open === "meta" ? (
          <MetaConnector status={status.data} failed={status.isError} />
        ) : null}
        {open === "telegram" ? (
          <TelegramConnector
            status={telegramStatus.data}
            refetch={telegramStatus.refetch}
          />
        ) : null}
      </CardContent>
    </Card>
  );
};

type TelegramStatus = {
  configured: boolean;
  bot_username: string | null;
  linked: boolean;
  sent_today: number;
  last_sent_at: string | null;
};
type TelegramChat = {
  id: number;
  title: string | null;
  type: string | null;
  enabled: boolean;
};
type TelegramEvents = Record<
  "new_lead" | "overdue_task" | "no_action_24h" | "no_leads",
  boolean
>;
const TELEGRAM_EVENTS = [
  "new_lead",
  "overdue_task",
  "no_action_24h",
  "no_leads",
] as const;

const useTelegramStatus = () =>
  useQuery({
    queryKey: ["telegram_connector_status"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "telegram_connector_status",
      );
      if (error) throw error;
      return data as TelegramStatus;
    },
    refetchInterval: 30_000,
    retry: false,
  });

const TelegramConnector = ({
  status,
  refetch,
}: {
  status: TelegramStatus | undefined;
  refetch: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { data: chats = [], refetch: refetchChats } = useGetList<TelegramChat>(
    "telegram_chats",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const { data: settingsRow, refetch: refetchSettings } = useGetOne<{
    id: string;
    value: { events?: Partial<TelegramEvents> };
  }>("connector_settings", { id: "telegram" });
  const events = settingsRow?.value?.events ?? {};

  const link = async () => {
    const { data, error } = await getSupabaseClient().rpc("telegram_link_code");
    if (error || !status?.bot_username) {
      notify("crm.settings.connectors.telegram.link_error", {
        type: "error",
        messageArgs: { error: error?.message ?? "bot" },
      });
      return;
    }
    window.open(
      `https://t.me/${status.bot_username}?start=${data}`,
      "_blank",
      "noopener",
    );
    setTimeout(refetch, 15_000);
  };

  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const invite = async () => {
    const { data, error } = await getSupabaseClient().rpc(
      "telegram_invite_code",
    );
    if (error || !status?.bot_username) {
      notify("crm.settings.connectors.telegram.link_error", {
        type: "error",
        messageArgs: { error: error?.message ?? "bot" },
      });
      return;
    }
    const url = `https://t.me/${status.bot_username}?start=inv_${data}`;
    setInviteLink(url);
    try {
      await navigator.clipboard.writeText(url);
      notify("crm.settings.connectors.telegram.invite_copied");
    } catch {
      // the link stays visible to copy by hand
    }
  };

  const toggleEvent = async (key: (typeof TELEGRAM_EVENTS)[number]) => {
    await dataProvider.update("connector_settings", {
      id: "telegram",
      data: {
        value: {
          ...(settingsRow?.value ?? {}),
          events: { ...events, [key]: events[key] === false },
        },
        updated_at: new Date().toISOString(),
      },
      previousData: settingsRow,
    });
    refetchSettings();
  };

  const toggleChat = async (chat: TelegramChat) => {
    await dataProvider.update("telegram_chats", {
      id: chat.id,
      data: { enabled: !chat.enabled },
      previousData: chat,
    });
    refetchChats();
  };

  if (!status?.configured) {
    return (
      <div className="rounded-xl border p-5 text-sm text-muted-foreground">
        {translate("crm.settings.connectors.telegram.not_configured")}
      </div>
    );
  }

  return (
    <div className="space-y-6 rounded-xl border p-5">
      <section className="space-y-2 text-sm">
        <p>
          {translate("crm.settings.connectors.telegram.bot", {
            bot: `@${status.bot_username}`,
          })}{" "}
          ·{" "}
          {translate("crm.settings.connectors.telegram.sent_today", {
            count: status.sent_today,
          })}
        </p>
        {status.linked ? (
          <p className="text-[#17703c]">
            {translate("crm.settings.connectors.telegram.linked")}
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" onClick={link}>
              <Send className="mr-1 h-4 w-4" />
              {translate("crm.settings.connectors.telegram.link")}
            </Button>
            <span className="text-muted-foreground">
              {translate("crm.settings.connectors.telegram.link_hint")}
            </span>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">
          {translate("crm.settings.connectors.telegram.chats")}
        </h3>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" onClick={invite}>
            <Link2 className="mr-1 h-4 w-4" />
            {translate("crm.settings.connectors.telegram.invite")}
          </Button>
          {inviteLink ? (
            <div className="flex flex-col gap-1 text-xs">
              <span>
                {translate("crm.settings.connectors.telegram.invite_person")}{" "}
                <code className="rounded bg-muted px-2 py-1 break-all">
                  {inviteLink}
                </code>
              </span>
              <span>
                {translate("crm.settings.connectors.telegram.invite_group")}{" "}
                <code className="rounded bg-muted px-2 py-1 break-all">
                  {inviteLink.replace("?start=", "?startgroup=")}
                </code>
              </span>
            </div>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          {translate("crm.settings.connectors.telegram.invite_hint")}
        </p>
        {chats.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.connectors.telegram.no_chats")}
          </p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {chats.map((chat) => (
              <li key={chat.id} className="flex items-center gap-3 px-3 py-2">
                <Switch
                  checked={chat.enabled}
                  onCheckedChange={() => toggleChat(chat)}
                />
                <span className="flex-1">
                  {chat.title ??
                    translate("crm.settings.connectors.telegram.private")}
                </span>
                {chat.enabled ? null : (
                  <span className="rounded-full bg-[#fbefd2] px-2 py-0.5 text-xs font-semibold text-[#7a4f00]">
                    {translate("crm.settings.connectors.telegram.waiting")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">
          {translate("crm.settings.connectors.telegram.events")}
        </h3>
        <ul className="space-y-2">
          {TELEGRAM_EVENTS.map((key) => (
            <li key={key} className="flex items-center gap-3 text-sm">
              <Switch
                checked={events[key] !== false}
                onCheckedChange={() => toggleEvent(key)}
              />
              {translate(`crm.settings.connectors.telegram.event.${key}`)}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          {translate("crm.settings.connectors.telegram.events_hint")}
        </p>
      </section>
    </div>
  );
};

const useMetaStatus = () =>
  useQuery({
    queryKey: ["meta_connector_status"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "meta_connector_status",
      );
      if (error) throw error;
      return data as MetaStatus;
    },
    refetchInterval: 60_000,
    retry: false,
  });

type TileState = "on" | "off" | "error" | "soon";

const ConnectorTile = ({
  icon,
  name,
  hint,
  state,
  active,
  onClick,
}: {
  icon: ReactNode;
  name: string;
  hint: string;
  state: TileState;
  active?: boolean;
  onClick?: () => void;
}) => {
  const translate = useTranslate();
  return (
    <button
      type="button"
      disabled={!onClick}
      onClick={onClick}
      aria-expanded={onClick ? active : undefined}
      className={cn(
        "flex flex-col gap-2 rounded-xl border bg-card p-4 text-left transition-colors",
        onClick ? "hover:border-primary" : "cursor-default opacity-70",
        active && "border-primary ring-1 ring-primary",
      )}
    >
      <div className="flex items-center gap-2">
        <span className="flex size-9 items-center justify-center rounded-lg bg-accent text-primary">
          {icon}
        </span>
        <span className="font-semibold">{name}</span>
      </div>
      <span className="text-xs text-muted-foreground">{hint}</span>
      <span
        className={cn(
          "w-fit rounded-full px-2 py-0.5 text-xs font-semibold",
          state === "on" && "bg-[#e3f1e8] text-[#17703c]",
          state === "error" && "bg-[#f3dcd8] text-[#a82f23]",
          (state === "off" || state === "soon") &&
            "bg-muted text-muted-foreground",
        )}
      >
        {translate(`crm.settings.connectors.state.${state}`)}
      </span>
    </button>
  );
};

const minutesAgo = (iso: string | undefined | null) =>
  iso ? Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000)) : null;

const MetaConnector = ({
  status,
  failed,
}: {
  status: MetaStatus | undefined;
  failed: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { data: forms = [], refetch: refetchForms } = useGetList<MetaForm>(
    "meta_forms",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
    },
  );
  const { data: settingsRow, refetch: refetchSettings } = useGetOne<{
    id: string;
    value: MetaSettings;
  }>("connector_settings", { id: "meta" });
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });

  const [formDraft, setFormDraft] = useState<Record<string, Partial<MetaForm>>>(
    {},
  );
  const [settings, setSettings] = useState<MetaSettings>({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (settingsRow) setSettings(settingsRow.value ?? {});
  }, [settingsRow]);

  const routing = settings.routing ?? "round_robin";
  // forms Meta keeps but no longer takes leads into
  const isLive = (form: MetaForm) => (form.status ?? "ACTIVE") === "ACTIVE";
  const liveForms = forms.filter(isLive);
  const archivedForms = forms.filter((form) => !isLive(form));
  const participants = settings.participants ?? [];
  const formValue = (form: MetaForm) => ({ ...form, ...formDraft[form.id] });
  const setForm = (form: MetaForm, change: Partial<MetaForm>) =>
    setFormDraft({
      ...formDraft,
      [form.id]: { ...formDraft[form.id], ...change },
    });
  const toggleParticipant = (id: Identifier, checked: boolean) =>
    setSettings({
      ...settings,
      participants: checked
        ? [...participants, Number(id)]
        : participants.filter((p) => p !== Number(id)),
    });

  const save = async () => {
    setSaving(true);
    try {
      for (const form of forms) {
        const change = formDraft[form.id];
        if (!change) continue;
        await dataProvider.update<MetaForm>("meta_forms", {
          id: form.id,
          data: {
            enabled: change.enabled ?? form.enabled,
            label: (change.label ?? form.label)?.trim() || null,
          },
          previousData: form,
        });
      }
      await dataProvider.update("connector_settings", {
        id: "meta",
        data: { value: settings, updated_at: new Date().toISOString() },
        previousData: settingsRow,
      });
      setFormDraft({});
      await Promise.all([refetchForms(), refetchSettings()]);
      notify("crm.settings.connectors.saved");
    } catch {
      notify("crm.settings.save_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const checked = minutesAgo(status?.state?.synced_at);
  const lastLead = minutesAgo(status?.last_lead_at);

  return (
    <div className="space-y-6 rounded-xl border p-5">
      <section className="space-y-1 text-sm">
        {failed ? (
          <p className="text-muted-foreground">
            {translate("crm.settings.connectors.meta.no_status")}
          </p>
        ) : !status?.configured ? (
          <p className="text-muted-foreground">
            {translate("crm.settings.connectors.meta.not_configured")}
          </p>
        ) : (
          <>
            <p>
              {checked != null
                ? translate("crm.settings.connectors.meta.checked", {
                    time: formatAgo(checked, translate),
                  })
                : translate("crm.settings.connectors.meta.never_checked")}
            </p>
            <p className="text-muted-foreground">
              {translate("crm.settings.connectors.meta.counts", {
                today: status.today,
                week: status.week,
              })}
              {lastLead != null
                ? ` · ${translate("crm.settings.connectors.meta.last_lead", {
                    time: formatAgo(lastLead, translate),
                  })}`
                : null}
            </p>
            {status.state?.last_error ? (
              <p className="font-semibold text-destructive">
                {translate("crm.settings.connectors.meta.error", {
                  error: status.state.last_error,
                })}
              </p>
            ) : null}
          </>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="font-semibold">
            {translate("crm.settings.connectors.meta.forms")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.connectors.meta.forms_hint")}
          </p>
        </div>
        {forms.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.connectors.meta.no_forms")}
          </p>
        ) : (
          <>
            {(
              [
                [liveForms, false],
                [archivedForms, true],
              ] as const
            ).map(([list, archived]) =>
              list.length === 0 ? null : (
                <details
                  key={String(archived)}
                  open={!archived}
                  className={cn(archived && "text-muted-foreground")}
                >
                  {archived ? (
                    <summary className="cursor-pointer text-sm">
                      {translate("crm.settings.connectors.meta.archived", {
                        smart_count: list.length,
                      })}
                    </summary>
                  ) : (
                    <summary className="sr-only">
                      {translate("crm.settings.connectors.meta.forms")}
                    </summary>
                  )}
                  <ul className="mt-2 divide-y rounded-lg border">
                    {list.map((form) => {
                      const value = formValue(form);
                      return (
                        <li
                          key={form.id}
                          className="flex flex-wrap items-center gap-3 px-3 py-2"
                        >
                          <Switch
                            checked={value.enabled}
                            onCheckedChange={(enabled) =>
                              setForm(form, { enabled })
                            }
                            aria-label={translate(
                              "crm.settings.connectors.meta.take_leads",
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <Input
                              value={value.label ?? ""}
                              placeholder={form.name ?? form.id}
                              onChange={(event) =>
                                setForm(form, { label: event.target.value })
                              }
                              className={cn(!value.enabled && "opacity-50")}
                            />
                            {value.label ? (
                              <span className="text-xs text-muted-foreground">
                                Meta: {form.name}
                              </span>
                            ) : null}
                          </div>
                          <span className="w-28 text-right text-sm tabular-nums text-muted-foreground">
                            {translate(
                              "crm.settings.connectors.meta.form_week",
                              {
                                smart_count: status?.forms?.[form.id] ?? 0,
                              },
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </details>
              ),
            )}
          </>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="font-semibold">
          {translate("crm.settings.connectors.meta.routing")}
        </h3>
        <div className="inline-flex rounded-xl border bg-card p-1 text-sm">
          {(["round_robin", "none"] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={routing === value}
              onClick={() => setSettings({ ...settings, routing: value })}
              className={cn(
                "rounded-lg px-3.5 py-1.5 font-semibold",
                routing === value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground",
              )}
            >
              {translate(`crm.settings.connectors.meta.routing_${value}`)}
            </button>
          ))}
        </div>
        {routing === "round_robin" ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.connectors.meta.participants_hint")}
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {sales.map((sale) => (
                <label
                  key={sale.id}
                  className="flex items-center gap-2 text-sm"
                >
                  <Checkbox
                    checked={participants.includes(Number(sale.id))}
                    onCheckedChange={(value) =>
                      toggleParticipant(sale.id, value === true)
                    }
                  />
                  {sale.first_name} {sale.last_name}
                </label>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.connectors.meta.routing_none_hint")}
          </p>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">
          {translate("crm.settings.connectors.meta.alert")}
        </h3>
        <label className="flex items-center gap-2 text-sm">
          <Input
            type="number"
            min={0}
            className="w-24"
            value={settings.alert_hours ?? 0}
            onChange={(event) =>
              setSettings({
                ...settings,
                alert_hours: Math.max(0, Number(event.target.value) || 0),
              })
            }
          />
          {translate("crm.settings.connectors.meta.alert_hours")}
        </label>
        <p className="text-xs text-muted-foreground">
          {translate("crm.settings.connectors.meta.alert_hint")}
        </p>
      </section>

      <div className="flex justify-end">
        <Button type="button" onClick={save} disabled={saving}>
          <Save className="mr-1 h-4 w-4" />
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};

const formatAgo = (
  minutes: number,
  translate: ReturnType<typeof useTranslate>,
) =>
  minutes < 60
    ? translate("crm.settings.connectors.minutes_ago", { smart_count: minutes })
    : minutes < 60 * 48
      ? translate("crm.settings.connectors.hours_ago", {
          smart_count: Math.round(minutes / 60),
        })
      : translate("crm.settings.connectors.days_ago", {
          smart_count: Math.round(minutes / 1440),
        });
